import {skipToken} from '@reduxjs/toolkit/query';
import {AlertProps, BreadcrumbType, DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';

import {
  PurchasePriceRuleRequestBody,
  PurchasePriceRuleScope,
  useGetPurchasePriceRuleQuery,
  useListPurchasePriceRulesQuery,
  useUpdatePurchasePriceRuleMutation,
} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {PurchasePriceRuleForm} from '../../components/PurchasePriceRuleForm/PurchasePriceRuleForm';
import {usePurchasePriceRuleScopeLabels} from '../../hooks/usePurchasePriceRuleScopeLabels';
import {formatPurchasePriceRuleScope} from '../../utils/formatPurchasePriceRuleScope';

const RuleIdSchema = z.string().uuid();
const FIRST_POSITION = 1;
const NO_SCOPE: PurchasePriceRuleScope = {
  makes: [],
  modelFamilies: [],
  trims: [],
};

export function PurchasePriceRuleDetail() {
  const params = useRequiredParams<{id: string}>();
  const parsedId = RuleIdSchema.safeParse(params.id);
  const ruleId = parsedId.success ? parsedId.data : null;
  const [canRead, canManage] = usePermissions({
    permissionKeys: ['readPurchasePriceRules', 'managePurchasePriceRules'],
  });

  const isRuleReadable = canRead && isNotNil(ruleId);

  const {
    data: rule,
    isLoading,
    isError,
  } = useGetPurchasePriceRuleQuery(isRuleReadable ? {id: ruleId} : skipToken);
  const {data: rules} = useListPurchasePriceRulesQuery(undefined, {
    skip: !isRuleReadable,
  });
  const [updatePurchasePriceRule] = useUpdatePurchasePriceRuleMutation();

  const getLabel = usePurchasePriceRuleScopeLabels(rule?.scope ?? NO_SCOPE);

  const breadcrumbs: BreadcrumbType[] = [
    {
      label: i18n.t('page.settings.purchasePrice.title'),
      href: settingsRoutes.purchasePrice,
    },
  ];

  const alerts: AlertProps[] | undefined = canManage
    ? undefined
    : [
        {
          type: 'banner',
          variant: 'info',
          message: i18n.t('page.settings.purchasePrice.ruleEditor.readOnlyNotice'),
        },
      ];

  const subtitle = match({rule, rules})
    .with({rule: {system: true}}, () =>
      i18n.t('page.settings.purchasePrice.ruleEditor.header.systemSubtitle')
    )
    .with(
      {rule: {scope: Pattern.not(Pattern.nullish)}, rules: Pattern.not(Pattern.nullish)},
      ({rule: customRule, rules: allRules}) =>
        i18n.t('page.settings.purchasePrice.ruleEditor.header.subtitle', {
          position: allRules.findIndex((item) => item.id === customRule.id) + FIRST_POSITION,
          total: allRules.length,
          scope: formatPurchasePriceRuleScope({
            scope: customRule.scope,
            getLabel,
            allModelFamiliesLabel: i18n.t(
              'page.settings.purchasePrice.marginRules.scope.allModelFamilies'
            ),
            allTrimsLabel: i18n.t('page.settings.purchasePrice.marginRules.scope.allTrims'),
          }),
        })
    )
    .otherwise(() => undefined);

  const title = rule?.system
    ? i18n.t('page.settings.purchasePrice.marginRules.systemRule.title')
    : rule?.name;

  const handleSave = (id: string, body: PurchasePriceRuleRequestBody) =>
    updatePurchasePriceRule({id, purchasePriceRuleRequestBody: body}).unwrap();

  return (
    <SettingsTemplate
      header={{title, subtitle, breadcrumbs, alerts}}
      headerVariant="v2"
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.purchasePriceRuleDetail('page')}
    >
      <DataStatus
        isEmpty={!canRead || isNil(ruleId)}
        emptyMessage={
          canRead
            ? i18n.t('general.notifications.noResults')
            : i18n.t('general.notifications.noPermission')
        }
        data-testid={testIds.settings.purchasePriceRuleDetail('ruleStatus')}
      >
        <Show when={rule}>
          {(rule) => (
            <PurchasePriceRuleForm
              key={rule.id}
              source={{type: 'rule', rule}}
              isReadOnly={!canManage}
              onSave={(body) => handleSave(rule.id, body)}
              baseTestId={testIds.settings.purchasePriceRuleDetail}
            />
          )}
        </Show>
      </DataStatus>
    </SettingsTemplate>
  );
}
